import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Plus, Save, Trash2 } from "lucide-react";
import { AdminShell } from "./Dashboard";
import ImageUpload from "./ImageUpload";
import { DEFAULT_CONTENT } from "@/lib/content";
import { api } from "@/lib/api";
import { SECTION_SCHEMAS } from "./sectionSchemas";

export default function ContentEditor() {
    const { key } = useParams();
    const navigate = useNavigate();
    const schema = SECTION_SCHEMAS[key];
    const [value, setValue] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/content").then(({ data }) => {
            const fromBackend = data?.[key];
            const base = DEFAULT_CONTENT[key] || {};
            setValue({ ...base, ...(fromBackend || {}) });
        });
    }, [key]);

    if (!schema) {
        return (
            <AdminShell>
                <div className="p-12">
                    <p className="text-xv-text-secondary">
                        Section inconnue : {key}
                    </p>
                    <Link
                        to="/admin"
                        className="mt-4 inline-flex items-center gap-2 text-xv-gold"
                    >
                        <ArrowLeft size={16} /> Retour
                    </Link>
                </div>
            </AdminShell>
        );
    }
    if (!value) {
        return (
            <AdminShell>
                <div className="p-12 text-xv-text-secondary/60 italic">
                    Chargement…
                </div>
            </AdminShell>
        );
    }

    const updateField = (k, v) => setValue((cur) => ({ ...cur, [k]: v }));

    const save = async () => {
        setErr("");
        setSaving(true);
        try {
            await api.put(`/admin/content/${key}`, { value });
            setSavedAt(new Date());
        } catch (e) {
            setErr(
                e?.response?.data?.detail || "Échec de l'enregistrement"
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminShell>
            <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
                {/* Header */}
                <div className="mb-10">
                    <Link
                        to="/admin"
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-xv-text-secondary/70 hover:text-xv-midnight"
                    >
                        <ArrowLeft strokeWidth={1.3} size={13} />
                        Toutes les sections
                    </Link>
                    <h1
                        className="mt-6 font-serif text-[34px] md:text-[44px] leading-tight text-xv-black"
                        style={{ fontWeight: 300 }}
                    >
                        {schema.label}
                    </h1>
                    {schema.description && (
                        <p className="mt-3 text-[15px] text-xv-text-secondary/80 leading-relaxed font-light max-w-2xl italic">
                            {schema.description}
                        </p>
                    )}
                </div>

                {/* Fields */}
                <div className="space-y-10 bg-xv-cream border border-xv-line p-8 md:p-12">
                    {schema.fields.map((field) => (
                        <FieldRenderer
                            key={field.key}
                            field={field}
                            value={value[field.key]}
                            onChange={(v) => updateField(field.key, v)}
                            sectionKey={key}
                        />
                    ))}
                </div>

                {/* Footer actions */}
                <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                    <div className="text-[12px] text-xv-text-secondary/60">
                        {savedAt && (
                            <span>
                                Enregistré à{" "}
                                {savedAt.toLocaleTimeString("fr-FR")} ✓
                            </span>
                        )}
                        {err && (
                            <span className="text-red-700 italic">{err}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase text-xv-text-secondary hover:text-xv-midnight"
                        >
                            <Eye strokeWidth={1.3} size={14} />
                            Voir le site
                        </button>
                        <button
                            type="button"
                            data-testid="content-save"
                            onClick={save}
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-6 py-3 text-[11px] tracking-[0.24em] uppercase border border-xv-midnight bg-xv-midnight text-xv-ivory hover:bg-xv-gold hover:border-xv-gold hover:text-xv-black transition-all disabled:opacity-50"
                        >
                            <Save strokeWidth={1.3} size={14} />
                            {saving ? "Enregistrement…" : "Enregistrer"}
                        </button>
                    </div>
                </div>
            </div>
        </AdminShell>
    );
}

function FieldRenderer({ field, value, onChange, sectionKey }) {
    if (field.kind === "text") {
        return (
            <div>
                <label className="overline text-xv-gold mb-3 block">
                    {field.label}
                </label>
                <input
                    type="text"
                    data-testid={`field-${sectionKey}-${field.key}`}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="xv-input w-full"
                />
            </div>
        );
    }

    if (field.kind === "textarea") {
        return (
            <div>
                <label className="overline text-xv-gold mb-3 block">
                    {field.label}
                </label>
                <textarea
                    data-testid={`field-${sectionKey}-${field.key}`}
                    value={value ?? ""}
                    rows={field.rows || 3}
                    onChange={(e) => onChange(e.target.value)}
                    className="xv-input w-full resize-y leading-relaxed font-serif"
                />
            </div>
        );
    }

    if (field.kind === "image") {
        return (
            <ImageUpload
                value={value ?? ""}
                onChange={onChange}
                label={field.label}
                testid={`field-${sectionKey}-${field.key}`}
            />
        );
    }

    if (field.kind === "string-list") {
        const list = Array.isArray(value) ? value : [];
        return (
            <div>
                <label className="overline text-xv-gold mb-3 block">
                    {field.label}
                </label>
                <div className="space-y-2">
                    {list.map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={item}
                                onChange={(e) => {
                                    const next = [...list];
                                    next[i] = e.target.value;
                                    onChange(next);
                                }}
                                className="xv-input flex-1"
                                data-testid={`field-${sectionKey}-${field.key}-${i}`}
                            />
                            <button
                                type="button"
                                onClick={() =>
                                    onChange(list.filter((_, idx) => idx !== i))
                                }
                                className="text-red-700/70 hover:text-red-800 p-2"
                                aria-label="Supprimer"
                            >
                                <Trash2 strokeWidth={1.3} size={14} />
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => onChange([...list, ""])}
                        className="mt-2 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-xv-gold hover:text-xv-goldDeep"
                    >
                        <Plus strokeWidth={1.3} size={13} />
                        Ajouter
                    </button>
                </div>
            </div>
        );
    }

    if (field.kind === "stat-list") {
        const items = Array.isArray(value) ? value : [];
        return (
            <div>
                <label className="overline text-xv-gold mb-3 block">
                    {field.label}
                </label>
                <div className="space-y-4">
                    {items.map((it, i) => (
                        <div
                            key={i}
                            className="grid grid-cols-[110px_110px_1fr_auto] gap-3 items-end"
                        >
                            <div>
                                <div className="text-[10px] tracking-[0.24em] uppercase text-xv-text-secondary/60 mb-1">
                                    Valeur
                                </div>
                                <input
                                    type="number"
                                    value={it.value ?? 0}
                                    onChange={(e) => {
                                        const next = [...items];
                                        next[i] = {
                                            ...it,
                                            value: Number(e.target.value),
                                        };
                                        onChange(next);
                                    }}
                                    className="xv-input w-full"
                                />
                            </div>
                            <div>
                                <div className="text-[10px] tracking-[0.24em] uppercase text-xv-text-secondary/60 mb-1">
                                    Suffixe
                                </div>
                                <input
                                    type="text"
                                    value={it.suffix ?? ""}
                                    onChange={(e) => {
                                        const next = [...items];
                                        next[i] = { ...it, suffix: e.target.value };
                                        onChange(next);
                                    }}
                                    placeholder=" ans+"
                                    className="xv-input w-full"
                                />
                            </div>
                            <div>
                                <div className="text-[10px] tracking-[0.24em] uppercase text-xv-text-secondary/60 mb-1">
                                    Label
                                </div>
                                <input
                                    type="text"
                                    value={it.label ?? ""}
                                    onChange={(e) => {
                                        const next = [...items];
                                        next[i] = { ...it, label: e.target.value };
                                        onChange(next);
                                    }}
                                    className="xv-input w-full"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() =>
                                    onChange(items.filter((_, idx) => idx !== i))
                                }
                                className="text-red-700/70 hover:text-red-800 p-2"
                                aria-label="Supprimer"
                            >
                                <Trash2 strokeWidth={1.3} size={14} />
                            </button>
                        </div>
                    ))}
                    {(!field.max || items.length < field.max) && (
                        <button
                            type="button"
                            onClick={() =>
                                onChange([
                                    ...items,
                                    { value: 0, suffix: "", label: "" },
                                ])
                            }
                            className="mt-2 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-xv-gold hover:text-xv-goldDeep"
                        >
                            <Plus strokeWidth={1.3} size={13} />
                            Ajouter une stat
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return null;
}
